import { envConfig } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import { validateId } from './utils';
import type {
  CreateDepositSlipRequestDto,
  DepositSlipCandidateDtoPaginationDto,
  DepositSlipDetailDto,
  DepositSlipItemsDto,
  UpdateDepositSlipDto,
} from '~/swagger/accounting/Api';

export type DepositSlipsFilter = {
  page?: number;
  pageSize?: number;
  bankAccountId?: string;
  'deposited.start'?: string;
  'deposited.end'?: string;
};

export type DepositSlipCandidatesFilter = {
  page?: number;
  pageSize?: number;
  bankAccountId: string;
  'transactionDate.start'?: string;
  'transactionDate.end'?: string;
  'finalizedDate.start'?: string;
  'finalizedDate.end'?: string;
};

const DEPOSIT_SLIPS_URL = `${envConfig.service.accounting}/company-portal/deposit-slips`;

export class DepositSlipsRepository extends BaseRestRepository {
  public async getDepositSlips(filter?: DepositSlipsFilter): Promise<DepositSlipItemsDto> {
    const url = urlWithQuery(DEPOSIT_SLIPS_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getDepositSlip(depositSlipId: string): Promise<DepositSlipDetailDto> {
    validateId(depositSlipId, 'Deposit slip');
    const url = `${DEPOSIT_SLIPS_URL}/${depositSlipId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async addDepositSlip(payload: CreateDepositSlipRequestDto): Promise<DepositSlipItemsDto> {
    const response = await this.fetchWithAuth(DEPOSIT_SLIPS_URL, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getDepositSlipCandidates(filter: DepositSlipCandidatesFilter): Promise<DepositSlipCandidateDtoPaginationDto> {
    validateId(filter.bankAccountId, 'Bank account');
    const url = urlWithQuery(`${DEPOSIT_SLIPS_URL}/candidates`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async updateDepositSlip(depositSlipId: string, payload: UpdateDepositSlipDto): Promise<DepositSlipDetailDto> {
    validateId(depositSlipId, 'Deposit slip');
    const url = `${DEPOSIT_SLIPS_URL}/${depositSlipId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteDepositSlip(depositSlipId: string): Promise<void> {
    validateId(depositSlipId, 'Deposit slip');
    await this.fetchWithAuth(`${DEPOSIT_SLIPS_URL}/${depositSlipId}`, {
      method: 'DELETE',
    });
  }
}

export const depositSlipsRepository = new DepositSlipsRepository();
